import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Pause, Play, X, Volume2, VolumeX, Music } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { BREATHING_TECHNIQUES, formatDuration, type BreathingTechniqueKey, type BreathingPhase } from '@/lib/breathing';
import { BreathingOrb } from '@/components/BreathingOrb';
import { BreathSound, loadSoundMode, saveSoundMode, SOUND_MODE_LABEL, type BreathSoundMode } from '@/lib/breathSound';
import { breathMusic, loadBreathTrack, loadMusicEnabled, saveMusicEnabled } from '@/lib/breathMusic';

const PHASE_INSTRUCTIONS: Record<string, string> = {
  inhale: 'Медленно через нос',
  short_inhale: 'Ещё короткий вдох',
  hold: 'Не дыши',
  exhale: 'Полностью через рот',
};

const NEXT_MODE: Record<BreathSoundMode, BreathSoundMode> = { bell: 'off', off: 'bell' };

/**
 * Экран самой дыхательной практики
 * 
 * URL: /breathing/:technique/run
 */
export default function BreathingExerciseScreen() {
  const nav = useNavigate();
  const { technique: techniqueKey } = useParams<{ technique: BreathingTechniqueKey }>();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();

  const technique = techniqueKey ? BREATHING_TECHNIQUES[techniqueKey] : null;

  // Состояние практики
  const [currentCycle, setCurrentCycle] = useState(1);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseSecondsLeft, setPhaseSecondsLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [totalSecondsElapsed, setTotalSecondsElapsed] = useState(0);
  const [soundMode, setSoundMode] = useState<BreathSoundMode>(loadSoundMode);
  
  const intervalRef = useRef<number | null>(null);
  const sessionSavedRef = useRef(false);
  // Звук практики — «чаша» на смену фазы (breathSound.ts). Один на весь экран.
  const soundRef = useRef<BreathSound | null>(null);
  if (!soundRef.current) soundRef.current = new BreathSound();
  soundRef.current.mode = soundMode;

  useEffect(() => {
    const sound = soundRef.current;
    return () => sound?.dispose();
  }, []);

  // Музыка под дыхание (breathMusic.ts): звук разбужен нажатием «Начать» на прошлом экране,
  // здесь он плавно выходит на фон. Ушли с экрана — затихает.
  const [musicOn, setMusicOn] = useState(loadMusicEnabled);
  const [hasMusic, setHasMusic] = useState(false);
  useEffect(() => {
    loadBreathTrack().then((t) => {
      setHasMusic(!!t);
      if (t && loadMusicEnabled()) breathMusic.start();
    });
    return () => breathMusic.stop(600);
  }, []);

  function toggleMusic() {
    haptic('light');
    const next = !musicOn;
    setMusicOn(next);
    saveMusicEnabled(next);
    if (next && !isPaused && !isFinished) breathMusic.start();
    else breathMusic.pause();
  }

  // Старт практики при загрузке
  useEffect(() => {
    if (!technique) return;
    setPhaseSecondsLeft(technique.phases[0].duration);
    haptic('medium');
    
    // Контекст звука уже разбужен нажатием «Начать» на прошлом экране — здесь только собираем цепочку
    ensureAudioContext().then(() => playPhaseTone(technique.phases[0].type, technique.phases[0].duration, true));
  }, [technique?.key]);

  async function ensureAudioContext() {
    await soundRef.current?.unlock();
  }

  function playPhaseTone(phaseType: string, duration: number, first = false) {
    soundRef.current?.phase(phaseType, duration, first);
  }

  // Главный таймер
  useEffect(() => {
    if (!technique || isPaused || isFinished) return;

    intervalRef.current = window.setInterval(() => {
      setPhaseSecondsLeft((prev) => {
        if (prev <= 1) {
          haptic('light');
          
          setCurrentPhaseIndex((phaseIdx) => {
            const nextPhaseIdx = phaseIdx + 1;
            
            if (nextPhaseIdx >= technique.phases.length) {
              setCurrentCycle((cycle) => {
                const nextCycle = cycle + 1;
                
                if (nextCycle > technique.recommendedCycles) {
                  finishSession(true);
                  return cycle;
                }
                
                haptic('medium');
                playPhaseTone(technique.phases[0].type, technique.phases[0].duration, true);
                return nextCycle;
              });
              return 0;
            }
            
            playPhaseTone(technique.phases[nextPhaseIdx].type, technique.phases[nextPhaseIdx].duration);
            return nextPhaseIdx;
          });
          
          return technique.phases[
            (currentPhaseIndex + 1) % technique.phases.length
          ].duration;
        }
        return prev - 1;
      });
      
      setTotalSecondsElapsed((s) => s + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [technique?.key, isPaused, isFinished, currentPhaseIndex]);

  async function finishSession(completed: boolean) {
    if (sessionSavedRef.current) return;
    sessionSavedRef.current = true;
    
    setIsFinished(true);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    
    if (completed) {
      haptic('success');
      soundRef.current?.finish();
    } else {
      soundRef.current?.hush();
    }
    breathMusic.stop(completed ? 3500 : 800);
    
    if (!profile || !technique) return;
    
    try {
      await supabase.from('breathing_sessions').insert({
        user_id: profile.id,
        technique: technique.key,
        cycles_planned: technique.recommendedCycles,
        cycles_completed: completed ? technique.recommendedCycles : currentCycle - 1,
        duration_seconds: totalSecondsElapsed,
        completed,
      });
    } catch (e) {
      console.error('[breathing] failed to save session:', e);
    }
  }

  async function togglePause() {
    haptic('light');
    await ensureAudioContext();
    if (musicOn) {
      if (isPaused) breathMusic.start(); else breathMusic.pause();
    }
    setIsPaused((p) => !p);
  }

  function stopSession() {
    haptic('medium');
    finishSession(false);
    setTimeout(() => nav('/chat?tab=relax'), 500);
  }

  // Кнопка включает и выключает «чашу»
  async function toggleSound() {
    haptic('light');
    const next = NEXT_MODE[soundMode];
    setSoundMode(next);
    saveSoundMode(next);
    const sound = soundRef.current;
    if (!sound) return;
    sound.mode = next;
    await ensureAudioContext();
  }

  // Запускаем audio context при первом тапе на экран
  useEffect(() => {
    const handler = () => ensureAudioContext();
    document.addEventListener('touchstart', handler, { once: true });
    document.addEventListener('click', handler, { once: true });
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('click', handler);
    };
  }, []);

  if (!technique) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-muted">
        Техника не найдена
      </div>
    );
  }

  const currentPhase: BreathingPhase = technique.phases[currentPhaseIndex];

  return (
    <div 
      className="min-h-screen flex flex-col max-w-md mx-auto"
      style={{
        background: `linear-gradient(160deg, ${technique.gradientFrom} 0%, ${technique.gradientTo} 100%)`,
      }}
    >
      {/* Header */}
      <div 
        className="px-5 py-4 flex items-center gap-3 border-b"
        style={{ borderColor: `${technique.color}1a` }}
      >
        <button
          onClick={() => nav('/chat?tab=relax')}
          className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors"
          aria-label="Назад"
        >
          <ChevronLeft size={18} className="text-white" />
        </button>
        <div className="flex-1">
          <div 
            className="text-[10px] uppercase tracking-[1.5px] font-medium mb-0.5"
            style={{ color: `${technique.color}cc` }}
          >
            {technique.name} · {technique.rhythmShort}
          </div>
          <div className="text-base font-display font-medium text-white">
            Цикл {Math.min(currentCycle, technique.recommendedCycles)} из {technique.recommendedCycles}
          </div>
        </div>
        {hasMusic && (
          <button
            onClick={toggleMusic}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors shrink-0"
            style={{
              background: musicOn ? `${technique.color}26` : 'rgba(255,255,255,0.06)',
              color: musicOn ? technique.color : 'rgba(255,255,255,0.4)',
            }}
            aria-label={musicOn ? 'Выключить музыку' : 'Включить музыку'}
            aria-pressed={musicOn}
          >
            <Music size={14} />
          </button>
        )}
        <button
          onClick={toggleSound}
          className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-[11px] font-medium transition-colors"
          style={{
            background: soundMode !== 'off' ? `${technique.color}26` : 'rgba(255,255,255,0.06)',
            color: soundMode !== 'off' ? technique.color : 'rgba(255,255,255,0.5)',
          }}
          aria-label={`Звук: ${SOUND_MODE_LABEL[soundMode]}. Нажми, чтобы сменить`}
        >
          {soundMode !== 'off' ? <Volume2 size={14} /> : <VolumeX size={14} />}
          {SOUND_MODE_LABEL[soundMode]}
        </button>
      </div>

      {/* Stage */}
      <div className="flex-1 px-5 py-6 flex flex-col items-center justify-center">
        
        {/* Команда фазы */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPhase?.label}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-[36px] font-display font-medium text-white tracking-wider mb-1.5"
            style={{ textShadow: `0 0 24px ${technique.colorRgba}` }}
          >
            {currentPhase?.label || 'Готов?'}
          </motion.div>
        </AnimatePresence>

        {/* Инструкция */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`inst-${currentPhase?.type}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-sm text-white/60 mb-8"
          >
            {currentPhase ? PHASE_INSTRUCTIONS[currentPhase.type] : ''}
          </motion.div>
        </AnimatePresence>

        {/* Сфера из частиц (13.10.2026) — вместо плоского круга с градиентом.
            Дышит вместе с фазой: вдох раздувает, задержка мерцает, выдох сжимает. */}
        <div className="relative w-[280px] h-[280px] flex items-center justify-center mb-6">
          <div className="absolute inset-0">
            <BreathingOrb
              size={280}
              color={technique.color}
              phase={currentPhase?.type ?? 'exhale'}
              duration={currentPhase?.duration ?? 4}
              phaseKey={`${currentCycle}-${currentPhaseIndex}`}
              paused={isPaused || isFinished}
            />
          </div>
          {/* Отсчёт поверх сферы */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentPhase?.type}-${phaseSecondsLeft}`}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.15 }}
              transition={{ duration: 0.2 }}
              className="relative z-10 text-[54px] font-display font-medium text-white tabular-nums tracking-wide"
              style={{ textShadow: '0 2px 18px rgba(0,0,0,0.65)' }}
            >
              {phaseSecondsLeft}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Минимальная подпись таймера */}
        <div className="text-xs text-white/40 tabular-nums tracking-wide">
          {formatDuration(totalSecondsElapsed)}
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 pb-24 pt-2 flex gap-2">
        <button
          onClick={togglePause}
          className="flex-1 py-3.5 rounded-2xl bg-white/[0.08] hover:bg-white/[0.13] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-all active:scale-[0.98]"
        >
          {isPaused ? <Play size={14} /> : <Pause size={14} />}
          {isPaused ? 'Продолжить' : 'Пауза'}
        </button>
        <button
          onClick={stopSession}
          className="flex-1 py-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] text-white/60 text-sm font-medium flex items-center justify-center gap-1.5 transition-all active:scale-[0.98]"
        >
          <X size={14} />
          Завершить
        </button>
      </div>

      {/* Финальная плашка */}
      {isFinished && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => nav('/chat?tab=relax')}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-bg-card rounded-3xl p-8 text-center max-w-sm w-full border"
            style={{ borderColor: `${technique.color}40` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-3">🎉</div>
            <div className="font-display font-bold text-xl mb-2 text-white">
              Отлично!
            </div>
            <div className="text-sm text-white/70 mb-1">
              {technique.name}
            </div>
            <div className="text-xs text-white/50 mb-4">
              {technique.recommendedCycles} циклов · {formatDuration(totalSecondsElapsed)}
            </div>
            <button
              onClick={() => nav('/chat?tab=relax')}
              className="w-full py-3 rounded-xl font-display font-bold text-sm"
              style={{ 
                background: technique.color,
                color: technique.gradientFrom,
              }}
            >
              Готово →
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
